import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";

import type { ControlInputCapabilitiesV1, ControlSchemePreferenceV1 } from "@gordonland/ui";
import { PLACEABLE_BLOCK_IDS_V1, type PlaceableBlockId } from "@gordonland/world-format";

import {
  CONTROL_SCHEME_CHOICES_ZH_CN_V1,
  CONTROL_SCHEME_COPY_ZH_CN_V1,
  supportsControlSchemeV1,
} from "./control-scheme.js";
import {
  DEVELOPMENT_DIAGNOSTIC_METRICS_V1,
  formatDevelopmentDiagnosticValueV1,
  type DevelopmentDiagnosticsSnapshotV1,
} from "./development-diagnostics.js";
import { BLOCK_LABELS } from "./shell-copy.js";

const ICON_FILE_BY_NAME = Object.freeze({
  blocks: "blocks",
  build: "build",
  close: "close",
  explore: "explore",
  jump: "jump",
  menu: "menu",
  next: "next",
  place: "place",
  presence: "presence",
  previous: "previous",
  remove: "remove",
  sprint: "sprint",
} as const);

type IconNameV1 = keyof typeof ICON_FILE_BY_NAME;

export function Icon({ name }: { readonly name: IconNameV1 }) {
  return <img className="ui-icon" src={`/assets/p0-022/icons/ui-${ICON_FILE_BY_NAME[name]}-v1.svg`} alt="" aria-hidden="true" />;
}

export function Modal({
  children,
  className = "",
  labelledBy,
  onClose,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly labelledBy: string;
  readonly onClose?: (() => void) | undefined;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const panel = panelRef.current;
    if (panel === null) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = [...panel.querySelectorAll<HTMLElement>("button:not(:disabled), [href], input:not(:disabled), [tabindex]:not([tabindex='-1'])")];
    focusable[0]?.focus();
    const keyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && onClose !== undefined) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    panel.addEventListener("keydown", keyDown);
    return () => {
      panel.removeEventListener("keydown", keyDown);
      previous?.focus();
    };
  }, [onClose]);
  return (
    <div className="modal-backdrop" role="presentation">
      <div ref={panelRef} className={`modal-panel ${className}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        {children}
      </div>
    </div>
  );
}

export function ControlSchemeDialog({
  capabilities,
  firstEntry,
  onClose,
  onSelect,
  selectedPreference,
}: {
  readonly capabilities: ControlInputCapabilitiesV1;
  readonly firstEntry: boolean;
  readonly onClose?: () => void;
  readonly onSelect: (preference: ControlSchemePreferenceV1) => void;
  readonly selectedPreference: ControlSchemePreferenceV1;
}) {
  return (
    <Modal labelledBy="control-scheme-heading" className="control-scheme-dialog" onClose={onClose}>
      {!firstEntry && onClose !== undefined ? (
        <button className="modal-close" type="button" aria-label="关闭操作方式" onClick={onClose}><Icon name="close" /></button>
      ) : null}
      <p className="eyebrow">GORDONLAND</p>
      <h1 id="control-scheme-heading">选择操作方式</h1>
      <p className="modal-detail">{CONTROL_SCHEME_COPY_ZH_CN_V1.detail}</p>
      <div className="scheme-options" role="group" aria-label="操作方式">
        {CONTROL_SCHEME_CHOICES_ZH_CN_V1.map((choice) => {
          const supported = supportsControlSchemeV1(capabilities, choice.id);
          return (
            <button
              key={choice.id}
              type="button"
              className="scheme-option"
              data-preference={choice.id}
              aria-pressed={selectedPreference === choice.id}
              disabled={!supported}
              onClick={() => onSelect(choice.id)}
            >
              <span className="scheme-option__mark" aria-hidden="true" />
              <span>
                <strong>{choice.label}</strong>
                <small>{choice.id === "auto" ? "根据当前可用设备与实际输入切换提示" : choice.id === "keyboard_mouse" ? "WASD、鼠标视角与快捷键" : "摇杆、触控视角与屏幕按钮"}</small>
                {!supported ? <em>{CONTROL_SCHEME_COPY_ZH_CN_V1.unsupported}</em> : null}
              </span>
            </button>
          );
        })}
      </div>
      {firstEntry ? <p className="selection-note">请选择后进入本地沙盒。游戏不会自动替你确认。</p> : null}
    </Modal>
  );
}

export function BuildTray({
  compact = false,
  onSelect,
  selectedBlockId,
}: {
  readonly compact?: boolean;
  readonly onSelect: (blockId: PlaceableBlockId) => void;
  readonly selectedBlockId: PlaceableBlockId;
}) {
  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number): void => {
    const delta = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const nextIndex = (index + delta + PLACEABLE_BLOCK_IDS_V1.length) % PLACEABLE_BLOCK_IDS_V1.length;
    const next = PLACEABLE_BLOCK_IDS_V1[nextIndex];
    if (next === undefined) return;
    onSelect(next);
    const parent = event.currentTarget.parentElement;
    parent?.querySelectorAll<HTMLButtonElement>("button")[nextIndex]?.focus();
  };
  return (
    <div className={compact ? "block-grid block-grid--compact" : "build-slots"} role="toolbar" aria-label="方块列表">
      {PLACEABLE_BLOCK_IDS_V1.map((blockId, index) => {
        const label = BLOCK_LABELS[index];
        if (label === undefined) return null;
        return (
          <button
            key={blockId}
            type="button"
            aria-label={label}
            aria-pressed={selectedBlockId === blockId}
            tabIndex={selectedBlockId === blockId ? 0 : -1}
            onClick={() => onSelect(blockId)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            <img src={`/assets/p0-022/icons/block-${String(blockId).padStart(2, "0")}-v1.svg`} alt="" aria-hidden="true" />
            {!compact ? <kbd>{blockId === 10 ? "0" : blockId === 11 ? "-" : blockId === 12 ? "=" : blockId}</kbd> : null}
          </button>
        );
      })}
    </div>
  );
}

export function GameMenu({
  onClose,
  onOpenControlScheme,
  onOpenPanel,
}: {
  readonly onClose: () => void;
  readonly onOpenControlScheme: () => void;
  readonly onOpenPanel: (panel: string) => void;
}) {
  const items = ["操作说明", "画面", "声音", "天气", "存储空间", "离开世界"] as const;
  return (
    <Modal labelledBy="game-menu-heading" className="game-menu" onClose={onClose}>
      <p className="eyebrow">本地沙盒</p>
      <h1 id="game-menu-heading">菜单</h1>
      <nav aria-label="游戏菜单">
        <button type="button" onClick={onClose}>继续游戏</button>
        <button type="button" onClick={() => onOpenPanel("操作说明")}>操作说明</button>
        <button type="button" onClick={onOpenControlScheme}>操作方式</button>
        {items.slice(1).map((item) => <button key={item} type="button" onClick={() => onOpenPanel(item)}>{item}</button>)}
      </nav>
    </Modal>
  );
}

export function InformationDialog({ heading, onClose }: { readonly heading: string; readonly onClose: () => void }) {
  const copy = heading === "操作说明"
    ? "键盘可使用 WASD 移动、鼠标控制视角、空格跳跃、B 键切换建造。触屏可使用左侧移动区与右侧视角区。"
    : heading === "画面"
      ? "画面会根据设备能力自动选择 WebGPU 或 WebGL2，并在性能不足时保持世界可操作。"
      : heading === "声音"
        ? "声音设置将在本地音频阶段启用；当前不会自动播放声音。"
        : heading === "天气"
          ? "昼夜与天气暂不可用。"
          : heading === "存储空间"
            ? "已保存在此设备。完整存储管理将在本地存档阶段启用。"
            : "当前世界仍保留在此设备；世界列表将在后续阶段启用。";
  return (
    <Modal labelledBy="information-heading" className="information-dialog" onClose={onClose}>
      <button className="modal-close" type="button" aria-label={`关闭${heading}`} onClick={onClose}><Icon name="close" /></button>
      <h1 id="information-heading">{heading}</h1>
      <p>{copy}</p>
      <button className="primary-button" type="button" onClick={onClose}>返回菜单</button>
    </Modal>
  );
}

const DIAGNOSTIC_GROUPS_V1 = ["帧与渲染", "物理与相机", "网络与版本", "音频与环境"] as const;

export function DiagnosticsHud({
  onClose,
  snapshot,
}: {
  readonly onClose: () => void;
  readonly snapshot: DevelopmentDiagnosticsSnapshotV1;
}) {
  return (
    <aside className="diagnostics-hud" aria-labelledby="diagnostics-heading">
      <header className="diagnostics-hud__header">
        <span>
          <small>DEV ONLY</small>
          <strong id="diagnostics-heading">诊断</strong>
        </span>
        <button type="button" aria-label="隐藏诊断面板" onClick={onClose}>隐藏</button>
      </header>
      <div className="diagnostics-hud__scroll">
        {DIAGNOSTIC_GROUPS_V1.map((group) => (
          <section key={group} aria-label={group}>
            <h2>{group}</h2>
            <dl>
              {DEVELOPMENT_DIAGNOSTIC_METRICS_V1.filter((metric) => metric.group === group).map((metric) => {
                const raw = snapshot.values[metric.id];
                return (
                  <div key={metric.id} data-diagnostic-row={metric.id}>
                    <dt><span>{metric.label}</span><code>{metric.id}</code></dt>
                    <dd>
                      <output
                        className={raw === null ? "diagnostics-hud__unsupported" : undefined}
                        data-diagnostic-id={metric.id}
                        data-diagnostic-raw={raw === null ? "unsupported" : String(raw)}
                        data-diagnostic-supported={raw === null ? "false" : "true"}
                      >
                        {formatDevelopmentDiagnosticValueV1(metric, raw)}
                      </output>
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>
        ))}
      </div>
    </aside>
  );
}
